<template>
  <!--
    声效选择块：
    - 容器样式沿用 WirelessSleepTime.vue 的 `bg-key rounded-4xl` 风格，
      每个块都自带 title 表明本块控制什么（按下声效 / 释放声效）。
    - 按钮使用固定宽度单行排列，超出容器后通过常驻的横向滚动条浏览。
    - 当前选中值由父组件按 mode 解析后传入；用户点击只回传「值」，由父组件负责按 mode 写左右侧。
  -->
  <div class="p-3 flex flex-col bg-key rounded-4xl gap-2">
    <h3 class="text-sm leading-5.5 font-medium text-black-white truncate">{{ title }}</h3>

    <!--
      按钮固定为单行排列；ScrollArea 使用 always 模式，让横向滚动条始终可见。
      内容区底部留出间距，避免滚动条覆盖按钮。
    -->
    <ScrollArea
      type="auto"
      scrollbar-orientation="horizontal"
      wheel-behavior="horizontal"
      thumb-class="bg-n-light-default"
      class="flex-1 min-h-0 w-full"
    >
      <div class="flex w-max gap-2">
        <Button
          v-for="option in options"
          :key="option.value"
          :ref="(el) => setButtonRef(option.value, el)"
          variant="fill"
          color="normal"
          shape="corner"
          size="sm"
          :active="option.value === value"
          :disabled="disabled"
          :class="
            cn(
              'shrink-0 w-20 h-8 p-0',
              option.value === value &&
                'bg-brand-blue1 text-white hover:bg-brand-blue1 hover:text-white active:bg-brand-blue1 active:text-white',
            )
          "
          @click="emit('commit', option.value)"
        >
          <span>{{ option.label }}</span>
        </Button>
      </div>
    </ScrollArea>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Button, ScrollArea, cn } from '@rapoo/element'

  const props = defineProps<{
    /** 块标题（例如「按下」「释放」） */
    title: string
    /** 当前模式（both / left / right）对应的展示值，用于高亮按钮 */
    value?: number
    /** 按钮显示的最小档位，默认 1 */
    min?: number
    /** 按钮显示的最大档位，默认 10 */
    max?: number
    disabled?: boolean
  }>()

  const emit = defineEmits<{
    /** 用户点击某个按钮时触发，载荷为新值；由父组件负责按 mode 写左右侧 */
    commit: [value: number]
  }>()

  const { t } = useI18n()

  const min = computed(() => props.min ?? 1)
  const max = computed(() => props.max ?? 10)

  /**
   * 「音效N」展示用的格式：未传 label 时回退为「音效N」。
   * 显式构造字符串模板，避免依赖 withDefaults 对函数类型默认值的支持。
   */
  const options = computed(() => {
    const result: Array<{ value: number; label: string }> = []
    for (let i = min.value; i <= max.value; i++) {
      result.push({
        value: i,
        label: i === 0 ? t('rt.common.off') : t('rt.sound.option', { value: i }),
      })
    }
    return result
  })

  /**
   * 把每个按钮对应的 DOM 元素收集起来，方便「进入视图时把选中按钮滚到可视区」。
   * 键是 option.value；卸载时清空对应键，避免被销毁的元素残留引用。
   */
  const buttonRefs = ref<Record<number, HTMLElement | null>>({})

  function setButtonRef(value: number, el: unknown) {
    if (!el) {
      buttonRefs.value[value] = null
      return
    }
    /**
     * `@rapoo/element` Button 渲染为单层元素，模板 ref 拿到的是组件实例（has $el）。
     * 这里既兼容组件实例、又兼容直接渲染的 HTMLElement；正常路径走 `$el`。
     */
    const dom = (el as { $el?: HTMLElement }).$el ?? (el as HTMLElement)
    buttonRefs.value[value] = dom ?? null
  }

  /**
   * 把当前 value 对应的按钮滚动到可视区。
   * - 用 `block: 'nearest'`：已可见时不会触发滚动，避免进入页面就把用户当前位置跳到中间；
   *   仅在选中按钮位于滚动容器可视区之外（例如 9 / 10）时才会把容器滚到它出现为止。
   * - `immediate=true` 表示「首次进入视图」，用 `auto` 关闭平滑动画，避免刚打开面板就有滚动动画的干扰感。
   */
  function scrollSelectedIntoView(immediate: boolean) {
    if (!props.value) return
    const el = buttonRefs.value[props.value]
    if (!el) return
    el.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: immediate ? 'auto' : 'smooth',
    })
  }

  /** 进入页面：等一帧让布局稳定后，无动画地把选中按钮滚入可视区 */
  onMounted(async () => {
    await nextTick()
    scrollSelectedIntoView(true)
  })

  /**
   * 后续 value 变化（SDK 推送 / 模式切换 / 「按下与释放同步」联动）：
   * 平滑地把目标按钮滚入可视区，已可见时 `block: 'nearest'` 不会产生动画。
   */
  watch(
    () => props.value,
    async () => {
      await nextTick()
      scrollSelectedIntoView(false)
    },
  )
</script>
